import { Button, Card, Divider, Group, Modal, Stack, Switch, Tabs, Text, Badge } from '@mantine/core'
import { Client } from '@/api/controlUnitApi/client'
import { Route } from '@/routes/hardware.$source.$tab'
import { IconRefresh } from '@tabler/icons-react'
import IconButton from '../Common/IconButton'
import { ReactNode, useState } from 'react'
import { PiSkull } from 'react-icons/pi'
import { useDisclosure } from '@mantine/hooks'
import useRestartsPanelData from './useRestartsPanelData'
import { noop } from 'lodash-es'
import FunctionUnavailable from '../Common/FunctionUnavailable'
import { useTranslation } from 'react-i18next'

const StackItem = ({
  label,
  icon,
  onToggle,
  hideRestart,
  tooltip,
  onRestart,
  checked,
  isLoading,
  statusBadge,
}: {
  label: string | ReactNode
  icon: ReactNode
  onToggle: (bool: boolean) => void
  tooltip: string
  onRestart: () => void
  checked: boolean
  hideRestart?: boolean
  isLoading?: boolean
  statusBadge?: ReactNode
}) => {
  const { t } = useTranslation('hardware')
  return (
    <Group justify="space-between" align="center">
      <Group align="center">
        {typeof label === 'string' ? <Text> {label} </Text> : label}
        {statusBadge}
      </Group>
      <Group>
        <Switch
          size="lg"
          onLabel="ON"
          offLabel="OFF"
          onChange={e => onToggle(e.target.checked)}
          checked={checked}
          disabled={isLoading}
        />
        {!hideRestart && (
          <IconButton
            label={isLoading ? `${tooltip} - ${t('deviceBusy')}` : tooltip}
            onClick={onRestart}
            icon={icon}
            disabled={isLoading}
          />
        )}
      </Group>
    </Group>
  )
}

const ServerStackItem = ({ label, actions }: { label: string | ReactNode; actions: ReactNode }) => {
  return (
    <>
      <Text> {label} </Text>
      {actions}
    </>
  )
}

enum Action {
  SHUT_DOWN = 'SHUT DOWN',
  FORCE_SHUT_DOWN = 'FORCE SHUT DOWN',
  RESTART = 'RESTART',
  FORCE_RESTART = 'FORCE RESTART',
}

const RestartsPanel = ({ client }: { client: Client }) => {
  const { source } = Route.useParams()
  const [isModalOpened, { open, close }] = useDisclosure(false)
  const [action, setAction] = useState<Action | null>(null)
  const [target, setTarget] = useState<'kit' | 'server'>('kit')
  const { t } = useTranslation(['hardware', 'common'])

  const {
    camerasState,
    beaconState,
    isTogglingBeacon,
    isTogglingCameras,
    isDeviceAvailable,
    toggleCameras,
    restartCameras,
    forceRestartServer,
    shutdownKit,
    forceShotDownKit,
    forceRestartKit,
    toggleBeacon,
  } = useRestartsPanelData({
    client,
    source,
  })

  const handleAction = (target: 'kit' | 'server') => (action: Action) => {
    setTarget(target)
    setAction(action)
    open()
  }

  const handleServerAction = (action: Action) => {
    handleAction('server')(action)
  }

  const handleKitAction = (action: Action) => {
    handleAction('kit')(action)
  }

  const getActionTranslation = (action: Action) => {
    switch (action) {
      case Action.SHUT_DOWN:
        return t('shutDown')
      case Action.FORCE_SHUT_DOWN:
        return t('forceShutDown')
      case Action.RESTART:
        return t('restart')
      case Action.FORCE_RESTART:
        return t('forceRestart')
      default:
        return ''
    }
  }

  const getTargetTranslation = (target: 'kit' | 'server') => {
    return target === 'kit' ? t('kit') : t('server')
  }

  const serverActions = (
    <Group>
      <FunctionUnavailable>
        <Button onClick={() => handleServerAction(Action.SHUT_DOWN)} disabled color="red">
          {t('shutDown')}
        </Button>
      </FunctionUnavailable>
      <FunctionUnavailable>
        <Button
          leftSection={<PiSkull size={16} />}
          onClick={() => handleServerAction(Action.FORCE_SHUT_DOWN)}
          disabled
          color="red"
        >
          {t('forceShutDown')}
        </Button>
      </FunctionUnavailable>
      <FunctionUnavailable>
        <Button onClick={() => handleServerAction(Action.RESTART)} disabled>
          {t('restart')}
        </Button>
      </FunctionUnavailable>
      <Button onClick={() => handleServerAction(Action.FORCE_RESTART)} disabled={!isDeviceAvailable('server')}>
        {t('forceRestart')}
      </Button>
    </Group>
  )

  const kitActions = (
    <Group>
      <Button onClick={() => handleKitAction(Action.SHUT_DOWN)} color="red">
        {t('shutDown')}
      </Button>
      <Button leftSection={<PiSkull size={16} />} onClick={() => handleKitAction(Action.FORCE_SHUT_DOWN)} color="red">
        {t('forceShutDown')}
      </Button>
      <FunctionUnavailable>
        <Button onClick={() => handleKitAction(Action.RESTART)} disabled>
          {t('restart')}
        </Button>
      </FunctionUnavailable>
      <Button onClick={() => handleKitAction(Action.FORCE_RESTART)}>{t('forceRestart')}</Button>
    </Group>
  )

  const determineModalConfirmAction = (action: Action, target: 'server' | 'kit') => {
    switch (`${action}_${target}`) {
      case `${Action.FORCE_RESTART}_server`:
        return () => forceRestartServer({})
      case `${Action.SHUT_DOWN}_kit`:
        return () => shutdownKit({})
      case `${Action.FORCE_SHUT_DOWN}_kit`:
        return () => forceShotDownKit({})
      case `${Action.FORCE_RESTART}_kit`:
        return () => forceRestartKit({})
      default:
        return () => {}
    }
  }

  return (
    <>
      <Tabs.Panel value="restarts">
        <Card shadow="sm" p="lg" radius="md" withBorder mt="lg">
          <Stack gap="md">
            <StackItem
              label={t('cameras')}
              icon={<IconRefresh />}
              tooltip={t('restartDevice', { device: t('cameras') })}
              checked={camerasState?.enabled ?? false}
              onToggle={enabled => toggleCameras({ body: { enabled } })}
              isLoading={!isDeviceAvailable('cameras') || isTogglingCameras}
              onRestart={() => restartCameras({})}
              statusBadge={
                !isDeviceAvailable('cameras') ? (
                  <Badge color="blue" variant="light" size="sm">
                    {t('restarting')}
                  </Badge>
                ) : undefined
              }
            />
            <Divider />
            <StackItem
              label={t('beacon')}
              icon={<IconRefresh />}
              onToggle={bool => toggleBeacon({ body: { enabled: bool } })}
              tooltip={t('restartBeacon')}
              hideRestart
              checked={beaconState?.enabled ?? false}
              onRestart={noop}
              isLoading={isTogglingBeacon}
            />
            <Divider />
            <ServerStackItem label={t('server')} actions={serverActions} />
            <Divider />
            <ServerStackItem label={t('kit')} actions={kitActions} />
          </Stack>
        </Card>
      </Tabs.Panel>
      <Modal opened={isModalOpened} onClose={close} title={t('confirmation')} centered zIndex={500}>
        <Text mb="md">
          {action &&
            t('confirmAction', {
              action: getActionTranslation(action),
              target: getTargetTranslation(target),
            })}
        </Text>
        <Button
          onClick={() => {
            if (!action) return
            determineModalConfirmAction(action, target)()
            close()
          }}
        >
          {t('confirm', { ns: 'common' })}
        </Button>
      </Modal>
    </>
  )
}

export default RestartsPanel
